import { motion, useReducedMotion } from 'motion/react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn.js';
import { EASE_OUT_QUINT } from '../motion.js';

/** O desenho em unidades do `viewBox`; o tamanho na tela vem da classe. */
const LADO = 168;
const ESPESSURA = 12;
const RAIO = (LADO - ESPESSURA) / 2;

/**
 * Anel: uma parte de um todo — o pago dentro do contratado (design.md §8,
 * Gráfico).
 *
 * O todo é o trilho do anel em `gray-data`, a referência; a parte é `ink`,
 * desenhada do alto no sentido horário ao entrar (500ms, na curva de saída).
 * O traço termina reto, sem ponta redonda: o sistema é de canto vivo. O que
 * vai no miolo — o número e o que ele quer dizer — vem de quem usa.
 *
 * O tamanho vem da classe e o traço acompanha: o mesmo anel serve ao
 * computador e, menor, ao celular. É imagem para o leitor de tela, com a
 * leitura inteira em `rotulo`. Com movimento reduzido, a parte já aparece
 * desenhada.
 */
export function Anel({
  fracao,
  rotulo,
  className,
  children,
}: {
  /** A parte, em [0, 1]. */
  fracao: number;
  /** O que o anel diz, por extenso, para o leitor de tela. */
  rotulo: string;
  /** O tamanho, em classe: `size-32 sm:size-42`. */
  className?: string | undefined;
  children?: ReactNode;
}) {
  const reduzir = useReducedMotion();
  const parte = Math.min(Math.max(fracao, 0), 1);

  return (
    <div role="img" aria-label={rotulo} className={cn('relative shrink-0', className)}>
      <svg viewBox={`0 0 ${LADO} ${LADO}`} className="size-full -rotate-90" fill="none" aria-hidden>
        <circle
          cx={LADO / 2}
          cy={LADO / 2}
          r={RAIO}
          strokeWidth={ESPESSURA}
          className="stroke-gray-data"
        />
        {parte > 0 ? (
          <motion.circle
            cx={LADO / 2}
            cy={LADO / 2}
            r={RAIO}
            strokeWidth={ESPESSURA}
            className="stroke-ink"
            initial={{ pathLength: reduzir ? parte : 0 }}
            animate={{ pathLength: parte }}
            transition={{ duration: 0.5, ease: EASE_OUT_QUINT, delay: 0.15 }}
          />
        ) : null}
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        {children}
      </div>
    </div>
  );
}
